import { useEffect, useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../contexts/ToastContext'
import AdminLayout from '../../components/admin/AdminLayout'
import ConfirmDialog from '../../components/admin/ConfirmDialog'
import { supabase } from '../../lib/supabase'
import {
  Search,
  Filter,
  UserPlus,
  Edit,
  Ban,
  CheckCircle,
  Key,
  Shield,
  AlertCircle,
  X
} from 'lucide-react'

interface User {
  id: string
  email: string
  full_name: string
  role: string
  is_suspended: boolean
  created_at: string
  education_points: number
  participation_points: number
}

export default function UsersManagement() {
  const { hasPermission, isSuperAdmin } = useAuth()
  const toast = useToast()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState<string>('all')
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [modalOpen, setModalOpen] = useState<'role' | 'suspend' | 'password' | 'create' | 'edit' | null>(null)
  const [roles, setRoles] = useState<any[]>([])

  useEffect(() => {
    loadUsers()
    loadRoles()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.id) throw new Error('No se pudo obtener el ID del usuario')
      
      // Usar la función RPC segura con UUID
      const { data, error } = await supabase
        .rpc('get_users_for_management', {
          requesting_user_uuid: user.id
        })

      if (error) throw error
      setUsers(data || [])
    } catch (error: any) {
      console.error('Error loading users:', error)
      toast.error(`Error al cargar usuarios: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  const loadRoles = async () => {
    try {
      const { data, error } = await supabase
        .from('roles')
        .select('*')
        .order('name')

      if (error) throw error
      setRoles(data || [])
    } catch (error: any) {
      console.error('Error loading roles:', error)
    }
  }

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesRole = filterRole === 'all' || user.role === filterRole
    return matchesSearch && matchesRole
  })

  const handleAssignRole = async (userId: string, roleName: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('assign-user-role', {
        body: {
          targetUserId: userId,
          roleName: roleName,
          action: 'assign'
        }
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error.message)

      toast.success('Rol asignado exitosamente')
      loadUsers()
      setModalOpen(null)
    } catch (error: any) {
      console.error('Error assigning role:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleSuspendUser = async (userId: string, suspend: boolean, reason?: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('suspend-user', {
        body: {
          targetUserId: userId,
          action: suspend ? 'suspend' : 'activate',
          reason: reason || 'Sin razón especificada'
        }
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error.message)

      toast.success(suspend ? 'Usuario suspendido exitosamente' : 'Usuario reactivado exitosamente')
      loadUsers()
      setModalOpen(null)
    } catch (error: any) {
      console.error('Error suspending user:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleResetPassword = async (userId: string, newPassword: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('reset-user-password', {
        body: {
          targetUserId: userId,
          newPassword: newPassword
        }
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error.message)

      toast.success('Contraseña reseteada exitosamente')
      setModalOpen(null)
    } catch (error: any) {
      console.error('Error resetting password:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleCreateUser = async (email: string, password: string, full_name: string, role: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email,
          password,
          full_name,
          role
        }
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error.message)

      toast.success('Usuario creado exitosamente')
      loadUsers()
      setModalOpen(null)
    } catch (error: any) {
      console.error('Error creating user:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  const handleUpdateUser = async (userId: string, email: string, full_name: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('update-user', {
        body: {
          targetUserId: userId,
          email,
          full_name
        }
      })

      if (error) throw error
      if (data?.error) throw new Error(data.error.message)

      toast.success('Usuario actualizado exitosamente')
      loadUsers()
      setModalOpen(null)
    } catch (error: any) {
      console.error('Error updating user:', error)
      toast.error(`Error: ${error.message}`)
    }
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Gestión de Usuarios
            </h1>
            <p className="text-gray-600">
              Administra usuarios, roles y permisos del sistema
            </p>
          </div>
          {hasPermission('manage_users') && (
            <button
              onClick={() => setModalOpen('create')}
              className="flex items-center space-x-2 px-4 py-2 bg-education text-white rounded-lg hover:bg-education/90"
            >
              <UserPlus className="h-4 w-4" />
              <span>Crear Usuario</span>
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Búsqueda */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por email o nombre..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            {/* Filtro por rol */}
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent appearance-none"
              >
                <option value="all">Todos los roles</option>
                <option value="user">Usuario</option>
                <option value="editor">Editor</option>
                <option value="moderator">Moderador</option>
                <option value="admin">Administrador</option>
                <option value="superadmin">Superadministrador</option>
              </select>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
            <span>
              Mostrando {filteredUsers.length} de {users.length} usuarios
            </span>
          </div>
        </div>

        {/* Tabla de usuarios */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-education mx-auto"></div>
              <p className="text-gray-600 mt-2">Cargando usuarios...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Puntos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name || 'Sin nombre'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          user.role === 'superadmin' ? 'bg-red-100 text-red-800' :
                          user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                          user.role === 'moderator' ? 'bg-yellow-100 text-yellow-800' :
                          user.role === 'editor' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div>Educación: {user.education_points || 0}</div>
                        <div>Participación: {user.participation_points || 0}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.is_suspended ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            Suspendido
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            Activo
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {hasPermission('manage_users') && (
                            <button
                              onClick={() => {
                                setSelectedUser(user)
                                setModalOpen('edit')
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                          {hasPermission('assign_roles') && (
                            <button
                              onClick={() => {
                                setSelectedUser(user)
                                setModalOpen('role')
                              }}
                              className="text-blue-600 hover:text-blue-900"
                              title="Cambiar rol"
                            >
                              <Shield className="h-4 w-4" />
                            </button>
                          )}
                          {hasPermission('suspend_users') && (
                            <button
                              onClick={() => {
                                setSelectedUser(user)
                                setModalOpen('suspend')
                              }}
                              className={user.is_suspended ? "text-green-600 hover:text-green-900" : "text-red-600 hover:text-red-900"}
                              title={user.is_suspended ? "Reactivar" : "Suspender"}
                            >
                              {user.is_suspended ? <CheckCircle className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                            </button>
                          )}
                          {hasPermission('reset_passwords') && (
                            <button
                              onClick={() => {
                                setSelectedUser(user)
                                setModalOpen('password')
                              }}
                              className="text-purple-600 hover:text-purple-900"
                              title="Resetear contraseña"
                            >
                              <Key className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modales */}
        {modalOpen === 'role' && selectedUser && (
          <RoleModal
            user={selectedUser}
            roles={roles}
            onClose={() => setModalOpen(null)}
            onSave={handleAssignRole}
          />
        )}

        {modalOpen === 'suspend' && selectedUser && (
          <SuspendModal
            user={selectedUser}
            onClose={() => setModalOpen(null)}
            onSave={handleSuspendUser}
          />
        )}

        {modalOpen === 'password' && selectedUser && (
          <PasswordModal
            user={selectedUser}
            onClose={() => setModalOpen(null)}
            onSave={handleResetPassword}
          />
        )}

        {modalOpen === 'create' && (
          <CreateUserModal
            roles={roles}
            onClose={() => setModalOpen(null)}
            onSave={handleCreateUser}
          />
        )}

        {modalOpen === 'edit' && selectedUser && (
          <EditUserModal
            user={selectedUser}
            onClose={() => setModalOpen(null)}
            onSave={handleUpdateUser}
          />
        )}
      </div>
    </AdminLayout>
  )
}

// Modal componentes auxiliares
function RoleModal({ user, roles, onClose, onSave }: any) {
  const [selectedRole, setSelectedRole] = useState(user.role)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Cambiar Rol de Usuario</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">Usuario: {user.email}</p>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
          >
            {roles.map((role: any) => (
              <option key={role.id} value={role.name}>
                {role.name} - {role.description}
              </option>
            ))}
          </select>
          <div className="flex space-x-3">
            <button
              onClick={() => onSave(user.id, selectedRole)}
              className="flex-1 bg-education text-white px-4 py-2 rounded-lg hover:bg-education/90"
            >
              Guardar
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function SuspendModal({ user, onClose, onSave }: any) {
  const [reason, setReason] = useState('')
  const isSuspended = user.is_suspended

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-4">
            {isSuspended ? 'Reactivar Usuario' : 'Suspender Usuario'}
          </h3>
          <p className="text-sm text-gray-600 mb-4">Usuario: {user.email}</p>
          {!isSuspended && (
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Razón de la suspensión..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 h-24"
            />
          )}
          <div className="flex space-x-3">
            <button
              onClick={() => onSave(user.id, !isSuspended, reason)}
              className={`flex-1 text-white px-4 py-2 rounded-lg ${
                isSuspended ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {isSuspended ? 'Reactivar' : 'Suspender'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function PasswordModal({ user, onClose, onSave }: any) {
  const [password, setPassword] = useState('')

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <h3 className="text-lg font-semibold mb-4">Resetear Contraseña</h3>
          <p className="text-sm text-gray-600 mb-4">Usuario: {user.email}</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Nueva contraseña (mín. 8 caracteres)"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4"
          />
          <div className="flex space-x-3">
            <button
              onClick={() => password.length >= 8 && onSave(user.id, password)}
              disabled={password.length < 8}
              className="flex-1 bg-education text-white px-4 py-2 rounded-lg hover:bg-education/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Resetear
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function CreateUserModal({ roles, onClose, onSave }: any) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [full_name, setFullName] = useState('')
  const [role, setRole] = useState('user')

  const handleSubmit = () => {
    if (!email || !password || password.length < 8) {
      return
    }
    onSave(email, password, full_name, role)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Crear Nuevo Usuario</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@ejemplo.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
              {password && password.length < 8 && (
                <p className="text-xs text-red-600 mt-1">La contraseña debe tener al menos 8 caracteres</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
              <input
                type="text"
                value={full_name}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Nombre y apellidos"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              >
                {roles.map((r: any) => (
                  <option key={r.id} value={r.name}>
                    {r.name} - {r.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleSubmit}
              disabled={!email || !password || password.length < 8}
              className="flex-1 bg-education text-white px-4 py-2 rounded-lg hover:bg-education/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Crear Usuario
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function EditUserModal({ user, onClose, onSave }: any) {
  const [email, setEmail] = useState(user.email)
  const [full_name, setFullName] = useState(user.full_name || '')

  const handleSubmit = () => {
    if (!email) {
      return
    }
    onSave(user.id, email, full_name)
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Editar Usuario</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Completo</label>
              <input
                type="text"
                value={full_name}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-education focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex space-x-3 mt-6">
            <button
              onClick={handleSubmit}
              disabled={!email}
              className="flex-1 bg-education text-white px-4 py-2 rounded-lg hover:bg-education/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Guardar Cambios
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
